import { DialogContentText } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import { FC, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, FormButton, ToastSeverity, useToast } from 'thermia-react-container';
import { RegistrationsContext } from '../../../context/contextProvider';
import { Registration } from '../../../types/registrationTypes';
import { ApiErrorHelper } from '../../../validation/Errors';

interface Props {
  selectedRegistration?: Registration | null;
  handleCancel: () => void;
}

type PageStatus = 'init' | 'success' | 'error' | 'locked';

const ReopenForm: FC<Props> = ({ handleCancel, selectedRegistration }) => {
  const { registrations: registrationsContext } = useContext(RegistrationsContext);
  const { activeRegistration } = registrationsContext;

  const { addToast } = useToast();
  const { registrations } = useContext(RegistrationsContext);
  const { reopenCompletedRegistrationAction } = registrations;
  const [pageStatus, setPageStatus] = useState<PageStatus>(activeRegistration?.canBeUnlocked ? 'init' : 'locked');
  const [loading, setLoading] = useState<boolean>(false);

  const { t } = useTranslation('registrations');
  const navigate = useNavigate();
  
  const handleReopenRegistration = async () => {
    if (!activeRegistration?.id) {
      return;
    }
    setLoading(true);
    try {
      await reopenCompletedRegistrationAction(activeRegistration?.id);
      setPageStatus('success');
    } catch (e) {
      if (ApiErrorHelper.isOnsiteVersionInvalid(e)) {
        addToast(t('registrations.general.errorOnsiteVersionIsInvalid'), ToastSeverity.ERROR);
      } else {
        setPageStatus('error');
      }
    }
    setLoading(false);
  };

  const goToRegistration = async () => {
    if (!activeRegistration?.id) {
      return;
    }
    const url = `/registrations/${activeRegistration.id}/customer`;
    navigate(url);
  };

  return (
    <Grid container direction="column" justifyContent="center" alignItems="center">
      {pageStatus === 'init' && (
        <>
          <DialogTitle>{t('registrations.general.editCompletedRegistration')}</DialogTitle>
          <DialogContent>
            <DialogContentText paragraph>{t('registrations.general.editCompletedRegistrationDescription')}</DialogContentText>
            <DialogContentText paragraph>{t('registrations.general.editCompletedRegistrationQuestion')}?</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancel} title={t('registrations.general.no')} />
            <FormButton
              variant={'contained'}
              onClick={handleReopenRegistration}
              title={t('registrations.general.yes')}
              loading={loading}
              style={{ marginLeft: '0px' }}
            />
          </DialogActions>
        </>
      )}

      {pageStatus === 'success' && (
        <>
          <DialogTitle>{t('registrations.general.success')}</DialogTitle>
          <DialogContent>
            <DialogContentText>{t('registrations.general.editCompletedRegistrationSuccessDescription')}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button title={t('registrations.general.close')} onClick={handleCancel} />
            <Button title={t('registrations.general.open')} variant={'contained'} onClick={goToRegistration} />
          </DialogActions>
        </>
      )}
      {pageStatus === 'error' && (
        <>
          <DialogTitle>{t('registrations.general.somethingWentWrong')}</DialogTitle>
          <DialogContent>
            <DialogContentText>{t('registrations.general.editCompletedRegistrationErrorDescription')}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button title={t('registrations.general.ok')} onClick={handleCancel} />
          </DialogActions>
        </>
      )}

      {pageStatus === 'locked' && (
        <>
          <DialogTitle>{t('registrations.general.editCompletedRegistrationLocked')}</DialogTitle>
          <DialogContent>
            <DialogContentText>{t('registrations.general.editCompletedRegistrationLockedDescription')}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button title={t('registrations.general.ok')} onClick={handleCancel} variant={'contained'} />
          </DialogActions>
        </>
      )}
    </Grid>
  );
};

export default ReopenForm;
